import {ClickerGameResultsScene} from '../../../ui/pages/ClickerGamePage/clicker-game/ClickerGameResultsScene';
import {DI_TOKENS} from '@/di/di.tokens';
import {uuid4} from '@sentry/utils';

import {injected} from 'brandi';

import {AbstractStorage} from '@specials/localstorage';
import {ProjectUserAuth, ProjectUserAuthStatus} from '@specials/project-user';

import {DAILY_STREAK_A_B_TEST_TOGGLE, LSKeys} from '@/constants';
import {DailyRewardsService} from '@/services/DailyRewardsService/DailyRewardsService';
import {DevToolsMbService} from '@/services/DevToolsMbService/DevToolsMbService';
import {ErrorsService} from '@/services/ErrorsService/ErrorsService';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {PlatformService} from '@/services/PlatformService/PlatformService';
import {IProjectApi} from '@/services/ProjectApi/types';
import {ProjectApiHeaders} from '@/services/ProjectAuthApi/ProjectApiHeaders';
import {ProjectUserInfoData, ProjectUserLoginData} from '@/services/ProjectAuthApi/types';
import {QueryStringService} from '@/services/QueryStringService/QueryStringService';
import {TooltipsService} from '@/services/TooltipsService/TooltipsService';
import {TracingService} from '@/services/TracingService/TracingService';
import {TrackingService} from '@/services/TrackingService/TrackingService';
import {GameOperationsService} from '@/services/shared/GameOperationsService/GameOperationsService';
import {GameStatusService} from '@/services/shared/GameStatusService/GameStatusService';
import {GiftsService} from '@/services/shared/GiftsService/GiftsService';
import {LeaderBoardService} from '@/services/shared/LeaderBoardService/LeaderBoardService';
import {OnboardingService} from '@/services/shared/OnboardingService/OnboardingService';
import {TasksService} from '@/services/shared/TasksService/TasksService';
import {ClickerStateStore} from '@/stores/ClickerStateStore/ClickerStateStore';
import {DailyGameStore} from '@/stores/DailyGameStore/DailyGameStore';
import {DailyRewardsStore} from '@/stores/DailyRewardsStore/DailyRewardsStore';
import {GameStatisticsStore} from '@/stores/GameStatisticsStore/GameStatisticsStore';
import {GameStatusStore} from '@/stores/GameStatusStore/GameStatusStore';
import {GiftsStore} from '@/stores/GiftsStore/GiftsStore';
import {ImprovementsStore} from '@/stores/ImprovementsStore/ImprovementsStore';
import {LeaderBoardStore} from '@/stores/LeaderBoardStore/LeaderBoardStore';
import {NavigationClickerModalStore} from '@/stores/NavigationClickerModalStore/NavigationClickerModalStore';
import {OnboardingStore} from '@/stores/OnboardingStore/OnboardingStore';
import {SettingsStore} from '@/stores/SettingsStore/SettingsStore';
import {ShareStore} from '@/stores/ShareStore/ShareStore';
import {TasksStore} from '@/stores/TasksStore/TasksStore';
import {TooltipsStore} from '@/stores/TooltipsStore/TooltipsStore';
import {UserStore} from '@/stores/UserStore/UserStore';
import {WinnersStore} from '@/stores/WinnersStore/WinnersStore';
import {Config} from '@/types';
import {ClickerAudioPlayer} from '@/ui/pages/ClickerGamePage/clicker-game/ClickerAudioPlayer';
import {ClickerGameAssetsLoader} from '@/ui/pages/ClickerGamePage/clicker-game/ClickerGameAssetsLoader';
import {ClickerGameScene} from '@/ui/pages/ClickerGamePage/clicker-game/ClickerGameScene';

import {GameStatisticsService} from '../GameStatisticsService/GameStatisticsService';

export class InitializeService {
  constructor(
    private readonly projectApi: IProjectApi,
    private readonly projectUserAuth: ProjectUserAuth<ProjectUserInfoData, ProjectUserLoginData>,
    private readonly config: Config,
    private readonly trackingService: TrackingService,
    private readonly loggerService: LoggerService,
    private readonly userStore: UserStore,
    private readonly settingsStore: SettingsStore,
    private readonly clickerStateStore: ClickerStateStore,
    private readonly dailyGameStore: DailyGameStore,
    private readonly dailyRewardsStore: DailyRewardsStore,
    private readonly gameStatusStore: GameStatusStore,
    private readonly giftsStore: GiftsStore,
    private readonly improvementsStore: ImprovementsStore,
    private readonly leaderBoardStore: LeaderBoardStore,
    private readonly navigationClickerModalStore: NavigationClickerModalStore,
    private readonly onboardingStore: OnboardingStore,
    private readonly shareStore: ShareStore,
    private readonly tasksStore: TasksStore,
    private readonly tooltipsStore: TooltipsStore,
    private readonly gameStatisticsStore: GameStatisticsStore,
    private readonly winnersStore: WinnersStore,
    private readonly platformService: PlatformService,
    private readonly projectApiHeaders: ProjectApiHeaders,
    private readonly tracingService: TracingService,
    private readonly localStorage: AbstractStorage,
    private readonly queryStringService: QueryStringService,
    private readonly tasksService: TasksService,
    private readonly giftsService: GiftsService,
    private readonly leaderBoardService: LeaderBoardService,
    private readonly devtoolsMbService: DevToolsMbService,
    private readonly dailyRewardsService: DailyRewardsService,
    private readonly onboardingService: OnboardingService,
    private readonly errorsService: ErrorsService,
    private readonly assetsLoader: ClickerGameAssetsLoader,
    private readonly clickerScene: ClickerGameScene,
    private readonly resultsClickerScene: ClickerGameResultsScene,
    private readonly gameOperationsService: GameOperationsService,
    private readonly clickerGameAudioPlayer: ClickerAudioPlayer,
    private readonly tooltipsService: TooltipsService,
    private readonly gameStatusService: GameStatusService,
    private readonly statisticsService: GameStatisticsService,
  ) {}

  async init(): Promise<void> {
    this.projectApiHeaders.setSessionIdData(uuid4());

    const token = this.queryStringService.getCustomSSOToken();

    if (token) {
      this.localStorage.setItem(LSKeys.CustomSSOToken, token);
    }

    this.platformService.init();

    void this.devtoolsMbService.initMBDevTools();

    if (this.platformService.isMb()) {
      this.tracingService.setMbData();
    }

    this.userStore.subscribe((userState) => {
      this.loggerService.log(userState);
    });

    this.projectApi.onUnauthorizedHttpErrorProject(() => {
      // если при запросе к api проекта получили HttpProjectError с кодом 401, разлогиним пользователя в модели
      this.userStore.setState({
        status: ProjectUserAuthStatus.Guest,
        data: undefined,
        error: undefined,
      });
    });

    this.projectApi.onHttpErrorBackend((error) => {
      // Договорились логировать в sentry все ошибки API,
      // которые не соответствуют контракту HttpErrorProject
      // @See: https://wiki.tcsbank.ru/display/DIGEDEV/%5BPS%5D+Meetings+%7C+Frontend
      this.loggerService.logHttpError(error);
    });

    this.projectUserAuth.subscribe((projectUserAuthState) => {
      this.userStore.setState(projectUserAuthState);

      this.trackingService.setPersonalizedLandingMetrics(
        projectUserAuthState.data?.userInfo.aBTestInfo?.[DAILY_STREAK_A_B_TEST_TOGGLE]?.flagMetadata || {},
      );
      this.trackingService.updateClientParams(projectUserAuthState.data?.userInfo?.user.id);

      if (this.userStore.isLoggedIn()) {
        this.trackingService.trackAuthUserValid();
        this.tracingService.setUserIdData(projectUserAuthState.data?.userInfo?.user.id);

        const settings = this.userStore.getSettings();
        if (settings) {
          this.settingsStore.setDefaultSettings(settings);
          this.clickerGameAudioPlayer.checkMusicMute();
          this.clickerGameAudioPlayer.checkSoundsMute();
        }

        this.winnersStore.updateWinner(projectUserAuthState.data?.userInfo?.user.id);

        // if (!isWebGLSupported()) {
        //   this.settingsStore.setPerformanceModeEnabled(true);
        //   this.settingsStore.hidePerformanceMode();
        // } else {
        //   this.settingsStore.setPerformanceModeEnabled(
        //     this.localStorage.getItem(LSKeys.PerformanceModeEnabled) === 'true',
        //   );
        // }

        this.settingsStore.setPerformanceModeEnabled(
          this.localStorage.getItem(LSKeys.PerformanceModeEnabled) === 'true',
        );

        void this.gameStatusService.fetchGameStatus();
      } else if (this.userStore.isError()) {
        const errorData = this.errorsService.buildErrorData(projectUserAuthState.error);
        this.trackingService.trackAuthError(errorData.breadcrumbs.errorCode);
      } else if (this.userStore.isGuest()) {
        this.resetStores();
      }

      // Для выполнения условия (в момент загрузки страницы):
      // отправляем при каждой загрузке каждой страницы одновременно с событием
      // pageload и в авторизованной зоне, и в неавторизованной зоне
      if (!this.userStore.isLoading()) {
        this.trackingService.trackLoadPage();
      }
    });

    // После того как подписались на изменения состояния авторизации, запускаем саму авторизацию.
    // Подписываемся на событие subscribeTrackingPageLoad только если страница не встроена в iframe, потому что
    // инициализация трекинга в iframe приводит к задвоению событий аналитики, поэтому в iframe трекинг не инициализируется
    // @See: https://wiki.tcsbank.ru/pages/viewpage.action?pageId=1900144132
    this.trackingService.subscribeTrackingModuleLoad(() => void this.projectUserAuth.auth());
  }

  public async initResultsClickerGame(container: HTMLDivElement): Promise<void> {
    await this.assetsLoader.loadBundle();

    await this.resultsClickerScene.create(container);
  }

  public async initCanvasClickerGame(container: HTMLDivElement): Promise<void> {
    await Promise.all([this.assetsLoader.loadBundle(), this.gameOperationsService.init()]);

    await this.clickerScene.create(container);

    this.onboardingService.initOnboardingStatus();

    if (!this.userStore.getIsDailyRewardAvailable()) {
      this.onboardingService.showMainOnboarding();
      this.onboardingService.attemptRestorePageWithOnboarding();
    }

    this.tooltipsService.init();
  }

  public async initHTMLClickerGame(): Promise<void> {
    await this.gameOperationsService.init();

    this.onboardingService.initOnboardingStatus();

    if (!this.userStore.getIsDailyRewardAvailable()) {
      this.onboardingService.showMainOnboarding();
      this.onboardingService.attemptRestorePageWithOnboarding();
    }

    this.tooltipsService.init();
  }

  public async initClickerInterfaces(): Promise<void> {
    await Promise.all([
      this.leaderBoardService.fetchLeague(),
      this.tasksService.fetchTasks(),
      this.giftsService.fetchGifts(),
    ]);

    // Задания нужно обновлять даже при первом открытии модалки, поэтому сбрасываем fetch status
    this.tasksService.clearTasksStatus();
    this.tasksService.clearTasksErrorData();

    if (this.userStore.getIsDailyRewardAvailable()) {
      void this.dailyRewardsService.fetchDailyRewards();
    }
  }

  public async initClickerInterfacesByBeforeResults(): Promise<void> {
    await Promise.all([
      this.giftsService.fetchGifts(),
      this.leaderBoardService.fetchLeaderBoard(),
      this.statisticsService.fetchStatistics(),
    ]);
  }

  public loginOnAuthPageInDevelopmentMode = (): void => {
    if (this.config.isLocal) {
      void this.projectUserAuth.auth({forceLogin: true});
    }
  };

  private resetStores = (): void => {
    this.settingsStore.resetState();
    this.clickerStateStore.resetState();
    this.dailyGameStore.resetState();
    this.dailyRewardsStore.resetState();
    this.gameStatusStore.resetState();
    this.giftsStore.resetState();
    this.improvementsStore.resetState();
    this.leaderBoardStore.resetState();
    this.navigationClickerModalStore.resetState();
    this.onboardingStore.resetState();
    this.shareStore.resetState();
    this.tasksStore.resetState();
    this.tooltipsStore.resetState();
    this.gameStatisticsStore.resetState();
    this.winnersStore.resetState();
  };
}

injected(
  InitializeService,
  DI_TOKENS.projectApi,
  DI_TOKENS.projectUserAuth,
  DI_TOKENS.config,
  DI_TOKENS.trackingService,
  DI_TOKENS.loggerService,
  DI_TOKENS.userStore,
  DI_TOKENS.settingsStore,
  DI_TOKENS.clickerStateStore,
  DI_TOKENS.dailyGameStore,
  DI_TOKENS.dailyRewardsStore,
  DI_TOKENS.gameStatusStore,
  DI_TOKENS.giftsStore,
  DI_TOKENS.improvementsStore,
  DI_TOKENS.leaderBoardStore,
  DI_TOKENS.navigationClickerModalStore,
  DI_TOKENS.onboardingStore,
  DI_TOKENS.shareStore,
  DI_TOKENS.tasksStore,
  DI_TOKENS.tooltipsStore,
  DI_TOKENS.gameStatisticsStore,
  DI_TOKENS.winnersStore,
  DI_TOKENS.platformService,
  DI_TOKENS.projectApiHeaders,
  DI_TOKENS.tracingService,
  DI_TOKENS.localStorageService,
  DI_TOKENS.queryStringService,
  DI_TOKENS.tasksService,
  DI_TOKENS.giftsService,
  DI_TOKENS.leaderBoardService,
  DI_TOKENS.devtoolsMbService,
  DI_TOKENS.dailyRewardsService,
  DI_TOKENS.onboardingService,
  DI_TOKENS.errorsService,
  DI_TOKENS.clickerGameAssetsLoader,
  DI_TOKENS.clickerGameScene,
  DI_TOKENS.clickerGameResultsScene,
  DI_TOKENS.gameOperationsService,
  DI_TOKENS.clickerGameAudioPlayer,
  DI_TOKENS.tooltipsService,
  DI_TOKENS.gameStatusService,
  DI_TOKENS.gameStatisticsService,
);
